import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, TrendingUp, BarChart3, FileText, Star, Share2,
  ChevronDown, Search, Filter, Palette, Check
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'metrics', label: 'Metrics', icon: TrendingUp },
  { key: 'professions', label: 'Professions', icon: Search },
  { key: 'content', label: 'Content', icon: FileText },
  { key: 'reviews', label: 'Reviews', icon: Star },
  { key: 'referrals', label: 'Referrals', icon: Share2 },
  { key: 'branding', label: 'Branding', icon: Palette },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState({ users: [], total: 0 });
  const [metrics, setMetrics] = useState(null);
  const [professions, setProfessions] = useState([]);
  const [contentStats, setContentStats] = useState(null);
  const [reviewStats, setReviewStats] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [brandingData, setBrandingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tierFilter, setTierFilter] = useState('');

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const res = await axios.get(`${API}/admin/overview`, { withCredentials: true });
        setOverview(res.data);
      } catch (e) {
        if (e.response?.status === 403) {
          setError('admin_denied');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, []);

  const fetchTabData = useCallback(async () => {
    try {
      if (tab === 'users') {
        const res = await axios.get(`${API}/admin/users`, { withCredentials: true, params: { tier: tierFilter || undefined } });
        setUsers(res.data);
      } else if (tab === 'metrics' && !metrics) {
        const res = await axios.get(`${API}/admin/metrics`, { withCredentials: true });
        setMetrics(res.data);
      } else if (tab === 'professions' && professions.length === 0) {
        const res = await axios.get(`${API}/admin/professions`, { withCredentials: true });
        setProfessions(res.data.professions || []);
      } else if (tab === 'content' && !contentStats) {
        const res = await axios.get(`${API}/admin/content-stats`, { withCredentials: true });
        setContentStats(res.data);
      } else if (tab === 'reviews' && !reviewStats) {
        const res = await axios.get(`${API}/admin/review-stats`, { withCredentials: true });
        setReviewStats(res.data);
      } else if (tab === 'referrals' && !referralStats) {
        const res = await axios.get(`${API}/admin/referral-stats`, { withCredentials: true });
        setReferralStats(res.data);
      } else if (tab === 'branding' && !brandingData) {
        const res = await axios.get(`${API}/admin/branding/logos`, { withCredentials: true });
        setBrandingData(res.data);
      }
    } catch (e) {
      // Admin tab fetch error — silently handled
    }
  }, [tab, tierFilter, metrics, professions.length, contentStats, reviewStats, referralStats, brandingData]);

  useEffect(() => {
    if (error) return;
    fetchTabData();
  }, [error, fetchTabData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F7] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error === 'admin_denied') {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-center px-6">
        <h2 className="font-display text-2xl text-[#3C3226] mb-2">Access Denied</h2>
        <p className="text-[#5C5347] text-center mb-6">You don't have admin privileges.</p>
        <button onClick={() => navigate('/dashboard')} className="rounded-none bg-[#3C3226] text-[#F0EBE3] px-8 py-3 font-medium">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F7]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#F7F4EF] border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Admin Dashboard</h1>
          <p className="text-xs text-gray-500">GetChosenFirst internal analytics</p>
        </div>
        <button onClick={() => navigate('/dashboard')} className="text-sm text-gray-500 hover:text-gray-900">
          Back to app
        </button>
      </header>

      {/* Tabs */}
      <div className="bg-[#F7F4EF] border-b border-gray-200 px-6 overflow-x-auto">
        <div className="flex gap-1">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === t.key
                  ? 'border-[#1D6B4F] text-[#1D6B4F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="p-6 max-w-6xl mx-auto">
        {tab === 'overview' && overview && <OverviewTab data={overview} />}
        {tab === 'users' && <UsersTab data={users} tierFilter={tierFilter} setTierFilter={setTierFilter} />}
        {tab === 'metrics' && metrics && <MetricsTab data={metrics} />}
        {tab === 'professions' && <ProfessionsTab data={professions} />}
        {tab === 'content' && contentStats && <ContentTab data={contentStats} />}
        {tab === 'reviews' && reviewStats && <ReviewsTab data={reviewStats} />}
        {tab === 'referrals' && referralStats && <ReferralsTab data={referralStats} />}
        {tab === 'branding' && brandingData && <BrandingTab data={brandingData} onUpdate={setBrandingData} />}
      </main>
    </div>
  );
}

function MetricCard({ label, value, sub, color = 'text-gray-900' }) {
  return (
    <div className="bg-[#F7F4EF] rounded-sm border border-gray-200 p-5" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
      <p className={`text-2xl font-semibold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function OverviewTab({ data }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard label="Total Users" value={data.total_users} sub={`+${data.this_week.new_users} this week`} />
      <MetricCard label="Paid Users" value={data.paid_users} sub={`+${data.this_week.new_paid} this week`} color="text-[#1D6B4F]" />
      <MetricCard label="Scans Run" value={data.total_scans} sub={`+${data.this_week.new_scans} this week`} />
      <MetricCard label="Revenue/Mo" value={`$${data.monthly_revenue.toLocaleString()}`} color="text-[#1D6B4F]" />
    </div>
  );
}

function UsersTab({ data, tierFilter, setTierFilter }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Filter size={14} className="text-gray-500" />
        <select
          value={tierFilter}
          onChange={e => setTierFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-none px-3 py-1.5 bg-[#F7F4EF]"
          data-testid="tier-filter"
        >
          <option value="">All tiers</option>
          <option value="free">Free</option>
          <option value="starter">Starter</option>
          <option value="professional">Professional</option>
        </select>
        <span className="text-xs text-gray-500">{data.total} users</span>
      </div>
      <div className="bg-[#F7F4EF] rounded-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Business</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Profession</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">City</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Tier</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Score</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.users?.map((u) => (
              <tr key={u.business_id || u.business_name} className="hover:bg-gray-50" data-testid={`user-row-${u.business_id || u.business_name}`}>
                <td className="px-4 py-3 font-medium text-gray-900">{u.business_name}</td>
                <td className="px-4 py-3 text-gray-600">{u.profession}</td>
                <td className="px-4 py-3 text-gray-600">{u.city}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    u.subscription_tier === 'professional' ? 'bg-[#1D6B4F]/10 text-[#1D6B4F]' :
                    u.subscription_tier === 'starter' ? 'bg-[#EF9F27]/10 text-[#EF9F27]' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {u.subscription_tier || 'free'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-900 font-medium">{u.score?.total || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetricsTab({ data }) {
  const maxScan = Math.max(...data.daily_scans.map(d => d.count), 1);
  return (
    <div className="space-y-6">
      {/* Funnel */}
      <div className="bg-[#F7F4EF] rounded-sm border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Conversion Funnel</h3>
        <div className="flex items-end gap-8">
          {[
            { label: 'Scans', value: data.funnel.scans },
            { label: 'Signups', value: data.funnel.signups },
            { label: 'Paid', value: data.funnel.paid },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-2xl font-semibold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Daily scans mini chart */}
      <div className="bg-[#F7F4EF] rounded-sm border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Daily Scans (30d)</h3>
        <div className="flex items-end gap-px h-24">
          {data.daily_scans.map((d) => (
            <div
              key={d.date}
              className="flex-1 bg-[#1D6B4F] rounded-t-sm min-h-[2px] transition-all hover:bg-[#1D6B4F]"
              style={{ height: `${Math.max((d.count / maxScan) * 100, 2)}%` }}
              title={`${d.date}: ${d.count} scans`}
            />
          ))}
        </div>
      </div>

      {/* Top professions + cities */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#F7F4EF] rounded-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Professions</h3>
          <div className="space-y-2">
            {data.top_professions.map((p) => (
              <div key={p.name} className="flex justify-between text-sm">
                <span className="text-gray-700">{p.name}</span>
                <span className="text-gray-500 font-medium">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#F7F4EF] rounded-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Cities</h3>
          <div className="space-y-2">
            {data.top_cities.map((c) => (
              <div key={c.name} className="flex justify-between text-sm">
                <span className="text-gray-700">{c.name}</span>
                <span className="text-gray-500 font-medium">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfessionsTab({ data }) {
  return (
    <div className="bg-[#F7F4EF] rounded-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Requested Professions</h3>
        <p className="text-xs text-gray-500">Every "Other" profession typed by users — sorted by demand</p>
      </div>
      {data.length === 0 ? (
        <p className="p-4 text-sm text-gray-500">No custom professions requested yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Typed Text</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Count</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">First Seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((p) => (
              <tr key={p.typed_text || p.value} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-900">{p.typed_text || p.value}</td>
                <td className="px-4 py-3 text-gray-600 font-medium">{p.count || 1}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{p.first_seen || p.created_at || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ContentTab({ data }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Total Generated" value={data.total} />
        <MetricCard label="Approved" value={data.approved} color="text-[#1D6B4F]" />
        <MetricCard label="Skipped" value={data.skipped} />
        <MetricCard label="Approval Rate" value={`${data.approval_rate}%`} color="text-[#1D6B4F]" />
      </div>
      {data.by_type?.length > 0 && (
        <div className="bg-[#F7F4EF] rounded-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">By Type</h3>
          <div className="space-y-2">
            {data.by_type.map((t) => (
              <div key={t.type || 'unknown'} className="flex justify-between text-sm">
                <span className="text-gray-700 capitalize">{t.type || 'unknown'}</span>
                <span className="text-gray-500">{t.approved}/{t.total} approved</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewsTab({ data }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard label="Total Requests" value={data.total_requests} />
      <MetricCard label="Pending" value={data.pending} />
      <MetricCard label="Sent" value={data.sent} color="text-[#1D6B4F]" />
      <MetricCard label="Opt-Outs" value={data.opt_outs} />
    </div>
  );
}

function ReferralsTab({ data }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard label="Reports Created" value={data.reports_created} />
      <MetricCard label="Total Views" value={data.total_views} />
      <MetricCard label="Clicks to Scan" value={data.clicks_to_scan} color="text-[#C67A5C]" />
      <MetricCard label="Signups from Refs" value={data.signups_from_referrals} color="text-[#1D6B4F]" />
    </div>
  );
}


function BrandingTab({ data, onUpdate }) {
  const [assignments, setAssignments] = useState(data.current || {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const logoMap = {};
  (data.logos || []).forEach(l => { logoMap[l.id] = l; });

  const handleAssign = (slot, logoId) => {
    setAssignments(prev => ({ ...prev, [slot]: logoId }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/branding/logos`, { assignments }, { withCredentials: true });
      onUpdate({ ...data, current: res.data.assignments });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const categories = [
    { key: 'primary', label: 'Primary Logos' },
    { key: 'tagline', label: 'Tagline Variants' },
    { key: 'mark', label: 'Beacon Marks' },
    { key: 'icon', label: 'App Icons' },
    { key: 'social', label: 'Social' },
    { key: 'editorial', label: 'Editorial Hero' },
  ];

  return (
    <div data-testid="branding-tab">
      {/* Save bar */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Logo Library</h2>
          <p className="text-xs text-gray-500 mt-1">Select which logo to use in each placement across the app.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2.5 text-xs font-medium uppercase tracking-wider flex items-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: saved ? '#1D6B4F' : '#2C2520', color: '#F0EBE3' }}
          data-testid="save-branding-btn"
        >
          {saving ? 'Saving...' : saved ? <><Check size={14} /> Saved</> : 'Save Branding'}
        </button>
      </div>

      {/* Logo Library Grid */}
      {categories.map(cat => {
        const logos = (data.logos || []).filter(l => l.category === cat.key);
        if (logos.length === 0) return null;
        return (
          <div key={cat.key} className="mb-8">
            <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-3">{cat.label}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {logos.map(logo => {
                const isUsed = Object.values(assignments).includes(logo.id);
                return (
                  <div key={logo.id} className="relative group" data-testid={`logo-card-${logo.id}`}>
                    <div
                      className="p-6 flex items-center justify-center transition-all"
                      style={{
                        backgroundColor: logo.id.includes('dark') || logo.id.includes('inverted') ? '#2C2520' : '#F7F4EF',
                        border: isUsed ? '2px solid #1D6B4F' : '1px solid #DDD5C8',
                        minHeight: 100,
                      }}
                    >
                      <img src={`/logos/${logo.file}`} alt={logo.label} className="max-h-12 max-w-full" />
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-[11px] text-gray-600">{logo.label}</span>
                      {isUsed && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5" style={{ backgroundColor: 'rgba(29,107,79,0.08)', color: '#1D6B4F' }}>Active</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Placement Assignments */}
      <div className="mt-8 pt-8" style={{ borderTop: '1px solid #DDD5C8' }}>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Placements</h2>
        <p className="text-xs text-gray-500 mb-6">Assign a logo to each slot. Changes take effect site-wide after saving.</p>
        <div className="space-y-4">
          {(data.placements || []).map(placement => (
            <div key={placement.slot} className="p-4 flex flex-col sm:flex-row sm:items-center gap-4" style={{ backgroundColor: '#FDFBF7', border: '1px solid #E8E2D8' }} data-testid={`placement-${placement.slot}`}>
              <div className="sm:w-48 flex-shrink-0">
                <p className="text-sm font-medium text-gray-900">{placement.label}</p>
                <p className="text-[11px] text-gray-500">{placement.description}</p>
              </div>
              <div className="flex-1 flex items-center gap-2 flex-wrap">
                {(data.logos || []).map(logo => {
                  const isSelected = assignments[placement.slot] === logo.id;
                  const isRecommended = placement.recommended?.includes(logo.id);
                  return (
                    <button
                      key={logo.id}
                      onClick={() => handleAssign(placement.slot, logo.id)}
                      className="relative p-2 transition-all"
                      style={{
                        backgroundColor: logo.id.includes('dark') || logo.id.includes('inverted') ? '#2C2520' : '#F7F4EF',
                        border: isSelected ? '2px solid #1D6B4F' : isRecommended ? '1px dashed #B8985A' : '1px solid #E8E2D8',
                        width: 72,
                        height: 48,
                      }}
                      title={logo.label}
                      data-testid={`assign-${placement.slot}-${logo.id}`}
                    >
                      <img src={`/logos/${logo.file}`} alt={logo.label} className="w-full h-full object-contain" />
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1D6B4F' }}>
                          <Check size={10} color="#fff" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Live Preview */}
      <div className="mt-8 pt-8" style={{ borderTop: '1px solid #DDD5C8' }}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {/* Light BG preview */}
          <div className="p-8" style={{ backgroundColor: '#EDE8DF', border: '1px solid #DDD5C8' }}>
            <p className="text-[9px] uppercase tracking-widest text-gray-500 mb-4">Sidebar / Light Background</p>
            {assignments.sidebar ? (
              <img src={`/logos/${logoMap[assignments.sidebar]?.file}`} alt="Sidebar" className="h-8" />
            ) : (
              <span className="text-sm text-gray-400 italic">No logo assigned</span>
            )}
          </div>
          {/* Dark BG preview */}
          <div className="p-8" style={{ backgroundColor: '#2C2520', border: '1px solid #3C3226' }}>
            <p className="text-[9px] uppercase tracking-widest mb-4" style={{ color: '#8C7E6E' }}>Navbar / Dark Background</p>
            {assignments.navbar_dark ? (
              <img src={`/logos/${logoMap[assignments.navbar_dark]?.file}`} alt="Navbar" className="h-8" />
            ) : (
              <span className="text-sm italic" style={{ color: '#5C5347' }}>No logo assigned</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
